import httpClient from '../lib/httpClient';
import type {
  FloatingIconEntry,
  FloatingIconCreateRequest,
  FloatingIconUpdateRequest,
  FloatingIconListParams,
  FloatingIconListResponse,
} from '../types/FloatingIcon';

const BASE_URL = '/api/v1/reels-cms/floating-icon';

/**
 * List floating-icon entries for the admin table, optionally filtered by tenant
 * or show, with pagination.
 */
export const listFloatingIcons = async (
  params: FloatingIconListParams = {}
): Promise<FloatingIconListResponse> => {
  try {
    const query = new URLSearchParams();
    if (params.tenant) query.append('tenant', params.tenant);
    if (params.showId) query.append('showId', params.showId);
    if (params.page !== undefined) query.append('page', params.page.toString());
    if (params.size !== undefined) query.append('size', params.size.toString());

    const queryString = query.toString();
    return await httpClient.get(queryString ? `${BASE_URL}?${queryString}` : BASE_URL);
  } catch (error) {
    console.error('Unable to fetch floating-icon entries');
    throw error;
  }
};

/**
 * Fetch a single floating-icon entry by id.
 */
export const getFloatingIcon = async (id: string): Promise<FloatingIconEntry> => {
  try {
    return await httpClient.get(`${BASE_URL}/${id}`);
  } catch (error) {
    console.error(`Unable to fetch floating-icon entry ${id}`);
    throw error;
  }
};

/**
 * Create a new floating-icon entry.
 */
export const createFloatingIcon = async (
  request: FloatingIconCreateRequest
): Promise<FloatingIconEntry> => {
  try {
    return await httpClient.post(BASE_URL, request);
  } catch (error) {
    console.error('Unable to create floating-icon entry');
    throw error;
  }
};

/**
 * Partially update a floating-icon entry — toggle enable/disable or edit the
 * window/icon. Omitted fields are left unchanged.
 */
export const updateFloatingIcon = async (
  id: string,
  request: FloatingIconUpdateRequest
): Promise<FloatingIconEntry> => {
  try {
    return await httpClient.put(`${BASE_URL}/${id}`, request);
  } catch (error) {
    console.error(`Unable to update floating-icon entry ${id}`);
    throw error;
  }
};

/**
 * Hard-delete a floating-icon entry. Disabling via updateFloatingIcon is the
 * normal path; delete is for permanent removal.
 */
export const deleteFloatingIcon = async (id: string): Promise<void> => {
  try {
    await httpClient.delete(`${BASE_URL}/${id}`, {});
  } catch (error) {
    console.error(`Unable to delete floating-icon entry ${id}`);
    throw error;
  }
};
